// ============================================================================
// Bids — the data layer. 2026-09-25.
//
// Every number these screens show is computed in the database: the three
// priced numbers by bid_price() / bid_suggest_price(), the quote by the table's
// own guard (sub ÷ (1 − margin), rounded), the review figures by
// bid_quote_summary(). Nothing here does pricing arithmetic.
//
// ⛔ Every write goes through commitWrite with .select() (PLAYBOOK §15).
// ⛔ Every loader registers against every table it reads, joined ones included
// (TCSD-0248) — READS below is that list.
// ============================================================================

import { supabase } from '../../shared/supabase';
import { commitWrite } from '../../shared/save-path';
import type { Database } from '../../types/database.types';

type Tables = Database['public']['Tables'];
type Views = Database['public']['Views'];
type Fns = Database['public']['Functions'];

export type BidRow = Tables['bidding_history']['Row'];
export type BidUpdate = Tables['bidding_history']['Update'];
/** The id is given by the database on insert (BID-…), so a new bid never carries one. */
export type BidInsert = Omit<Tables['bidding_history']['Insert'], 'bid_id'>;
export type SettingRow = Tables['bid_pricing_settings']['Row'];
export type ComparableRow = Views['v_bid_comparables']['Row'];
export type Suggestion = Fns['bid_suggest_price']['Returns'][number];
export type QuoteSummary = Fns['bid_quote_summary']['Returns'][number];
export type Reviewer = Fns['bid_reviewers']['Returns'][number];
export type FloorCareLineRow = Tables['bid_floor_care_lines']['Row'];
export type PriceLines = Fns['bid_price_lines']['Returns'][number];

/** Every table a bid screen reads, directly or through a view or function. */
export const READS = [
  'bidding_history',
  'bid_floor_care_lines',
  'service_catalog',
  'bid_pricing_settings',
  'facilities',
  'facility_details',
  'service_assignments',
  'accounts',
  'corporate_parents',
  'markets',
  'user_profiles',
  'employees',
] as const;

const TOUCHES_BID = ['bidding_history'];
const TOUCHES_SERVICES = ['bidding_history', 'bid_floor_care_lines'];
const TOUCHES_SETTINGS = ['bid_pricing_settings'];

export const FACILITY_TYPES: { value: string; label: string }[] = [
  { value: 'Surgery_Center', label: 'Surgery center' },
  { value: 'Dialysis', label: 'Dialysis clinic' },
  { value: 'MOB', label: 'Medical office building' },
  { value: 'Hospital', label: 'Hospital' },
  { value: 'Other', label: 'Other' },
];
export function typeLabel(t: string | null | undefined): string {
  return FACILITY_TYPES.find((f) => f.value === t)?.label ?? (t ?? '').replace(/_/g, ' ');
}

export const STATUS_WORDS: Record<string, string> = {
  Draft: 'Draft',
  Submitted: 'Waiting for executive review',
  Approved: 'Pricing approved',
  Withdrawn: 'Withdrawn',
  Won: 'Won',
  Lost: 'Lost',
  Expired: 'Expired',
};

async function must<T>(p: PromiseLike<{ data: unknown; error: unknown }>, what: string): Promise<T> {
  const { data, error } = await p;
  if (error) {
    const e = new Error(`Could not load ${what}.`);
    (e as Error & { cause?: unknown }).cause = error;
    throw e;
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function fetchBids(): Promise<BidRow[]> {
  return must<BidRow[]>(
    supabase.from('bidding_history').select('*').order('updated_at', { ascending: false }),
    'the bids',
  ).then((r) => r ?? []);
}

export async function fetchBid(bidId: string): Promise<BidRow | null> {
  const rows = await must<BidRow[]>(supabase.from('bidding_history').select('*').eq('bid_id', bidId), 'the bid');
  return rows?.[0] ?? null;
}

export async function fetchSuggestion(bidId: string): Promise<Suggestion | null> {
  const rows = await must<Suggestion[]>(supabase.rpc('bid_suggest_price', { p_bid_id: bidId }), 'the suggested price');
  return rows?.[0] ?? null;
}

export async function fetchQuoteSummary(bidId: string): Promise<QuoteSummary | null> {
  const rows = await must<QuoteSummary[]>(supabase.rpc('bid_quote_summary', { p_bid_id: bidId }), 'the price summary');
  return rows?.[0] ?? null;
}

/** The bid's monthly price, line by line, from the database (bid_price_lines). A draft
 *  prices answers on screen before they are saved; it takes exactly one bid. */
export async function fetchPriceLines(bidIds: string[], draft?: Record<string, unknown>): Promise<PriceLines[]> {
  if (!bidIds.length) return [];
  const rows = await must<PriceLines[]>(
    supabase.rpc('bid_price_lines', { p_bid_ids: bidIds, p_draft: (draft ?? null) as Fns['bid_price_lines']['Args']['p_draft'] }),
    'the monthly price',
  );
  return rows ?? [];
}

export async function fetchFloorCareLines(bidId: string): Promise<FloorCareLineRow[]> {
  const rows = await must<FloorCareLineRow[]>(
    supabase.from('bid_floor_care_lines').select('*').eq('bid_id', bidId).order('sort_order'),
    'the floor-care lines',
  );
  return rows ?? [];
}

/** The floor-care services, in the app's own list order and words. */
export async function fetchFloorServices(): Promise<{ code: string; name: string }[]> {
  const rows = await must<{ service_code: string; display_name: string }[]>(
    supabase.from('service_catalog').select('service_code, display_name').eq('division_id', 'FLOOR').eq('is_active', true).order('sort_order'),
    'the floor-care services',
  );
  return (rows ?? []).map((r) => ({ code: r.service_code, name: r.display_name }));
}

export function fetchReviewers(): Promise<Reviewer[]> {
  return must<Reviewer[]>(supabase.rpc('bid_reviewers'), 'the account managers').then((r) => r ?? []);
}

export async function fetchIsExecutive(): Promise<boolean> {
  const v = await must<boolean>(supabase.rpc('is_executive'), 'your seat');
  return v === true;
}

export function fetchSettings(): Promise<SettingRow[]> {
  return must<SettingRow[]>(
    supabase.from('bid_pricing_settings').select('*').order('set_at', { ascending: false }).order('setting_id', { ascending: false }),
    'the pricing settings',
  ).then((r) => r ?? []);
}

export interface Option { value: string; label: string; hint?: string | null }

export async function fetchMarkets(): Promise<Option[]> {
  const rows = await must<{ market_name: string; covers_states: string[] | null }[]>(
    supabase.from('markets').select('market_name, covers_states').eq('is_active', true).order('sort_order'),
    'the markets',
  );
  return (rows ?? []).map((m) => ({ value: m.market_name, label: m.market_name, hint: (m.covers_states ?? []).join(', ') }));
}

export async function fetchParents(): Promise<Option[]> {
  const rows = await must<{ parent_id: string; parent_name: string; status: string | null }[]>(
    supabase.from('corporate_parents').select('parent_id, parent_name, status').order('parent_name'),
    'the health systems',
  );
  return (rows ?? []).map((p) => ({ value: p.parent_id, label: p.parent_name }));
}

export async function fetchAccounts(): Promise<Option[]> {
  const rows = await must<{ account_id: string; account_name: string; parent_id: string | null }[]>(
    supabase.from('accounts').select('account_id, account_name, parent_id').order('account_name'),
    'the customers',
  );
  return (rows ?? []).map((a) => ({ value: a.account_id, label: a.account_name, hint: a.parent_id }));
}

/** The brand rule reads the customer's own flag (TCSD-0322) — never a copy. */
export async function fetchSuppliesOwnChemicals(accountId: string | null, parentId: string | null): Promise<boolean> {
  if (!accountId && !parentId) return false;
  let q = supabase.from('accounts').select('account_id').eq('supplies_own_chemicals', true);
  q = accountId ? q.eq('account_id', accountId) : q.eq('parent_id', parentId as string);
  const rows = await must<{ account_id: string }[]>(q.limit(1), 'the customer');
  return (rows ?? []).length > 0;
}

export async function fetchFacilityTypeValues(): Promise<string[]> {
  return FACILITY_TYPES.map((f) => f.value);
}

// ---------------------------------------------------------------------------
// Writes — each one proves it landed.
// ---------------------------------------------------------------------------

export async function createBid(fields: BidInsert): Promise<BidRow> {
  const rows = await commitWrite<BidRow>({
    label: 'start the bid',
    write: supabase.from('bidding_history').insert(fields as Tables['bidding_history']['Insert']).select('*'),
    touches: TOUCHES_BID,
  });
  return rows[0];
}

export async function updateBid(bidId: string, fields: BidUpdate, label: string): Promise<BidRow> {
  const rows = await commitWrite<BidRow>({
    label,
    write: supabase.from('bidding_history').update(fields).eq('bid_id', bidId).select('*'),
    touches: TOUCHES_BID,
  });
  return rows[0];
}

export async function priceBid(bidId: string, reset = false): Promise<BidRow> {
  const rows = await commitWrite<BidRow>({
    label: reset ? 'go back to the suggested numbers' : 'price the bid',
    write: supabase.rpc('bid_price', { p_bid_id: bidId, p_reset: reset }) as unknown as PromiseLike<{ data: BidRow[] | null; error: unknown }>,
    touches: TOUCHES_BID,
  });
  return rows[0];
}

/** Step 3's one save: the answers and the floor-care lines together, in one transaction. */
export async function saveServices(bidId: string, services: Record<string, unknown>, lines: Record<string, unknown>[]): Promise<BidRow> {
  const rows = await commitWrite<BidRow>({
    label: 'save what is in the contract',
    write: supabase.rpc('bid_save_services', {
      p_bid_id: bidId,
      p_services: services as Fns['bid_save_services']['Args']['p_services'],
      p_lines: lines as unknown as Fns['bid_save_services']['Args']['p_lines'],
    }) as unknown as PromiseLike<{ data: BidRow[] | null; error: unknown }>,
    touches: TOUCHES_SERVICES,
  });
  return rows[0];
}

export function setSetting(args: {
  scope: 'company' | 'facility_type' | 'market' | 'facility';
  key: string;
  value: number | null;
  facilityType?: string | null;
  marketRegion?: string | null;
  state?: string | null;
  facilityId?: string | null;
  until?: string | null;
  reason: string;
  clear?: boolean;
}) {
  return commitWrite({
    label: args.clear ? 'clear the rate' : 'save the rate',
    write: supabase.rpc('bid_set_setting', {
      p_scope: args.scope,
      p_key: args.key,
      p_value: (args.value ?? undefined) as number,
      p_facility_type: args.facilityType ?? undefined,
      p_market_region: args.marketRegion ?? undefined,
      p_state: args.state ?? undefined,
      p_facility_id: args.facilityId ?? undefined,
      p_until: args.until ?? undefined,
      p_reason: args.reason,
      p_clear: args.clear ?? false,
    } as Fns['bid_set_setting']['Args']),
    touches: TOUCHES_SETTINGS,
  });
}

/** The sterile shares the book records for buildings in the same OR band. The
 *  band itself comes from the database's one definition (bid_or_band). */
export async function fetchShareBand(facilityType: string, ors: number | null): Promise<{ or_band: string; low: number; high: number; n: number } | null> {
  if (!ors || ors <= 0) return null;
  const band = await must<string | null>(supabase.rpc('bid_or_band', { p_ors: ors }), 'the OR band');
  if (!band) return null;
  const rows = await must<{ sterile_share: number | null }[]>(
    supabase.from('v_bid_comparables').select('sterile_share').eq('facility_type', facilityType).eq('or_band', band)
      .eq('excluded_from_comps', false).not('sterile_share', 'is', null),
    'the comparable buildings',
  );
  const shares = (rows ?? []).map((r) => Number(r.sterile_share)).filter((n) => Number.isFinite(n));
  if (shares.length < 3) return null;
  return { or_band: band, low: Math.min(...shares), high: Math.max(...shares), n: shares.length };
}

/** The current value of one setting for this bid, by the database's own precedence. */
export async function fetchSettingFor(bid: BidRow, key: string): Promise<{ value: number; scope_label: string } | null> {
  const rows = await must<{ value: number; scope_label: string }[]>(
    supabase.rpc('bid_setting', {
      p_key: key,
      p_facility_type: bid.facility_type,
      p_market: bid.market_region ?? undefined,
      p_state: bid.prospect_state ?? undefined,
    } as Fns['bid_setting']['Args']),
    'a pricing setting',
  );
  return rows?.[0] ?? null;
}

export async function fetchFacilityNames(ids: string[]): Promise<Map<string, string>> {
  if (!ids.length) return new Map();
  const rows = await must<{ facility_id: string; facility_name: string }[]>(
    supabase.from('facilities').select('facility_id, facility_name').in('facility_id', ids),
    'the building names',
  );
  return new Map((rows ?? []).map((r) => [r.facility_id, r.facility_name]));
}

export async function fetchPeopleNames(eids: string[]): Promise<Map<string, string>> {
  if (!eids.length) return new Map();
  const rows = await must<{ employee_id: string; employee_name_first: string | null; employee_name_last: string | null }[]>(
    supabase.from('employees').select('employee_id, employee_name_first, employee_name_last').in('employee_id', eids),
    'who set the rates',
  );
  return new Map((rows ?? []).map((r) => [r.employee_id, [r.employee_name_first, r.employee_name_last].filter(Boolean).join(' ') || r.employee_id]));
}
